import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { DarkMode, LightMode } from '@mui/icons-material';
import type { LandingCopy, LandingLocale, LandingPalette, LandingThemeMode } from './landingCopy';

type LandingNavProps = {
  onSignIn: () => void;
  onNavigate: (id: string) => void;
  copy: LandingCopy['nav'];
  palette: LandingPalette;
  themeMode: LandingThemeMode;
  locale: LandingLocale;
  onToggleTheme: () => void;
  onToggleLocale: () => void;
};

const LandingNav: React.FC<LandingNavProps> = ({
  onSignIn,
  onNavigate,
  copy,
  palette,
  themeMode,
  locale,
  onToggleTheme,
  onToggleLocale,
}) => {
  const links = [
    { id: 'home', label: copy.home },
    { id: 'stats', label: copy.stats },
    { id: 'modules', label: copy.modules },
    { id: 'about', label: copy.about },
  ] as const;

  const isDark = themeMode === 'dark';

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: palette.navBg,
        backdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${palette.border}`,
        color: palette.text,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 }, gap: 1.5 }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ flexShrink: 0, cursor: 'pointer' }}
            onClick={() => onNavigate('home')}
          >
            <Box
              component="img"
              src="/image/Logo.jpeg"
              alt="ELOG"
              sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
            />
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Outfit", "Cairo", sans-serif',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: palette.text,
                }}
              >
                ELOG
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: palette.mutedSoft,
                  letterSpacing: '0.08em',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {locale === 'ar' ? 'سجل الملاحة الجوي الإلكتروني' : 'Aviation Electronic Logbook'}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={0.5}
            sx={{ display: { xs: 'none', md: 'flex' }, ml: 3, flex: 1 }}
          >
            {links.map((link) => (
              <Button
                key={link.id}
                onClick={() => onNavigate(link.id)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  color: palette.muted,
                  '&:hover': { color: palette.accent },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Stack>

          <Box sx={{ flex: 1, display: { xs: 'block', md: 'none' } }} />

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={isDark ? 'Light mode' : 'Dark mode'}>
              <IconButton
                onClick={onToggleTheme}
                size="small"
                sx={{
                  color: palette.text,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 2,
                }}
                aria-label="Toggle theme"
              >
                {isDark ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Button
              onClick={onToggleLocale}
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 0,
                px: 1.5,
                color: palette.text,
                border: `1px solid ${palette.border}`,
                borderRadius: 2,
              }}
            >
              {copy.langToggle}
            </Button>

            <Button
              variant="contained"
              onClick={onSignIn}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 2.5,
                borderRadius: 2,
                bgcolor: palette.primaryBtn,
                '&:hover': { bgcolor: palette.primaryBtnHover },
              }}
            >
              {copy.signIn}
            </Button>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default LandingNav;
